/**
 * TypeScript interfaces for Device Flow Authentication
 * 
 * These interfaces define the data structures and contracts for implementing
 * OAuth 2.0 Device Authorization Grant flow with Amazon Federate using oidc-client-ts.
 */

/**
 * User information extracted from authentication tokens
 */
export interface UserInfo {
  userAlias: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ldapGroups?: string[];
}

/**
 * JWT token set containing all authentication tokens
 */
export interface JWTTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Token set interface for managing authentication tokens
 */
export interface TokenSet {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Parameters for initiating device flow authentication
 */
export interface DeviceFlowInitiationParams {
  state?: string;
  clientRequestId?: string;
  redirectUri?: string;
}

/**
 * Response from successful token exchange
 */
export interface TokenResponse {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  userInfo: UserInfo;
}

/**
 * Result of authentication validation
 */
export interface AuthValidationResult {
  isValid: boolean;
  reason?: string;
  shouldClear: boolean;
}

/**
 * Result of authentication attempt
 */
export interface AuthResult {
  success: boolean;
  tokens?: TokenSet;
  userInfo?: UserInfo;
  error?: string;
}

/**
 * Device flow state for tracking authentication progress
 */
export interface DeviceFlowState {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  pollingInterval: number;
  expiresAt: number;
  status: 'pending' | 'authorized' | 'expired' | 'denied';
}

/**
 * Authentication context containing user information and auth type
 */
export interface UserContext {
  userAlias: string;
  isAdmin: boolean;
  authType: 'cognito' | 'federate';
  tokenExpiry: number;
  ldapGroups?: string[];
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Authentication manager interface
 */
export interface AuthManager {
  initializeAuth(): Promise<void>;
  authenticateUser(): Promise<AuthResult>;
  refreshTokens(): Promise<TokenSet>;
  logout(): Promise<void>;
  isAuthenticated(): boolean;
  getAuthType(): 'cognito' | 'federate' | null;
}

/**
 * Device flow handler interface using oidc-client-ts
 */
export interface DeviceFlowHandler {
  initiateDeviceFlow(params?: DeviceFlowInitiationParams): Promise<void>;
  handleCallback(authorizationCode: string): Promise<TokenResponse>;
  validateAuthenticationState(): Promise<AuthValidationResult>;
  refreshTokens(): Promise<JWTTokens>;
  clearInvalidAuthenticationState(): Promise<void>;
}

/**
 * Token manager interface for handling dual authentication types
 */
export interface TokenManager {
  storeTokens(tokens: TokenSet, type: 'cognito' | 'federate'): void;
  getTokens(type: 'cognito' | 'federate'): TokenSet | null;
  refreshAccessToken(type: 'cognito' | 'federate'): Promise<TokenSet>;
  clearTokens(type: 'cognito' | 'federate'): void;
  isTokenExpired(token: string): boolean;
  shouldRefreshToken(token: string): boolean;
  getStoredAuthType(): 'cognito' | 'federate' | null;
  ensureValidTokens(type: 'cognito' | 'federate'): Promise<TokenSet>;
}

/**
 * HTTP interceptor interface for authentication
 */
export interface AuthInterceptor {
  addAuthHeaders(request: any): any;
  handleAuthError(error: any): Promise<any>;
  retryWithRefresh(request: any): Promise<any>;
}

/**
 * Token storage keys for federate authentication
 * These match the implementation in the gz-device-flow branch
 */
export const FEDERATE_TOKEN_KEYS = {
  ID_TOKEN: 'federate_token',
  ACCESS_TOKEN: 'federate_accessToken', 
  REFRESH_TOKEN: 'federate_refreshToken'
} as const;

/**
 * Token storage keys for cognito authentication (existing)
 */
export const COGNITO_TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  ID_TOKEN: 'id_token', 
  REFRESH_TOKEN: 'refresh_token'
} as const;

/**
 * Authentication type enumeration
 */
export type AuthenticationType = 'cognito' | 'federate';

/**
 * Device type enumeration
 */
export type DeviceType = 'mobile' | 'desktop';